/* eslint-disable react/react-in-jsx-scope */
import React, { useContext, useMemo } from "react";
import { Context } from "../Store";
import {
  capitalize,
  getLongestGame,
  getTimeOfDay,
  makeLinkMarker,
  parseCompletedGames,
  REPLAY_TYPES,
  scrollToSection,
} from "../utils/helpers";
import styles from "./GameCommentary.module.scss";

const BOLD_OPEN = "[[B]]";
const BOLD_CLOSE = "[[/B]]";
const BR_MARKER = "[[BR]]";

const renderRichMarkers = (text) => {
  const markerRegex = /\[\[B\]\](.*?)\[\[\/B\]\]|\[\[L:([^\]|]+)\|(.*?)\]\]|\[\[BR\]\]/g;
  const parts = [];
  let lastIndex = 0;
  let match;
  let key = 0;

  while ((match = markerRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    if (match[0] === BR_MARKER) {
      parts.push(<br key={`br-${key++}`} />);
    } else if (match[1] !== undefined) {
      parts.push(
        <span key={`bold-${key++}`} className={styles.winnerName}>
          {match[1]}
        </span>,
      );
    } else {
      const linkTarget = match[2];
      const linkText = match[3];

      parts.push(
        <button
          key={`link-${key++}`}
          type="button"
          className={styles.mediaLink}
          onClick={(event) => scrollToSection(linkTarget, event)}
        >
          {linkText}
        </button>,
      );
    }

    lastIndex = markerRegex.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
};

const GameCommentary = () => {
  const [state] = useContext(Context);

  const completedGames = useMemo(() => parseCompletedGames(state?.newsfeed), [state.newsfeed]);

  const totalReplays = useMemo(() => {
    if (!state?.newsfeed?.length) return 0;

    return state.newsfeed.reduce(
      (count, event) => (REPLAY_TYPES.has(event.type) && event.video_url ? count + 1 : count),
      0,
    );
  }, [state.newsfeed]);

  const mediaCounts = useMemo(() => {
    if (!state?.newsfeed?.length) return { loopcams: 0, groupPhotos: 0, firstMedia: null };

    let loopcams = 0;
    let groupPhotos = 0;
    let firstMedia = null;
    const firstGameStartTime = completedGames[0]?.startTime;

    for (const event of state.newsfeed) {
      // Count Loop Cams (type 200)
      if (event.type === 200) {
        loopcams++;
        // Check if this is before first game and we haven't found first media yet
        if (!firstMedia && firstGameStartTime && new Date(event.date) < new Date(firstGameStartTime)) {
          firstMedia = { type: "Loop Cam", date: event.date };
        }
      }

      // Count Group Photos (type 201)
      if (event.type === 201) {
        groupPhotos++;
        // Check if this is before first game and we haven't found first media yet
        if (!firstMedia && firstGameStartTime && new Date(event.date) < new Date(firstGameStartTime)) {
          firstMedia = { type: "Group Photo", date: event.date };
        }
      }
    }

    return { loopcams, groupPhotos, firstMedia };
  }, [state.newsfeed, completedGames]);

  const commentary = useMemo(() => {
    if (!completedGames.length || !state?.parsedPlayers?.length) return "No completed games recorded.";

    const startDate = new Date(completedGames[0].startTime);
    const day = startDate.toLocaleDateString("en-GB", { weekday: "long" });
    const timeOfDay = getTimeOfDay(startDate);

    const lines = [];

    // Check for multiple oches
    const ocheList = state.oche
      ? state.oche
          .split(",")
          .map((o) => o.trim())
          .filter(Boolean)
      : [];

    const multipleOches = ocheList.length > 1;
    const sessionTypeText = state.type === 1 ? "tournament" : state.type === 3 ? "event" : "";
    const socialDartsOpening = `It was a lively ${day} ${timeOfDay} ${sessionTypeText ? `${sessionTypeText} ` : ""}session of Social Darts with ${completedGames.length} game${completedGames.length !== 1 ? "s" : ""} on the board.`;

    // Opening line with oche info
    if (state.type === 2) {
      lines.push(socialDartsOpening);
    } else if (multipleOches) {
      const ocheText = sessionTypeText
        ? `${sessionTypeText} spread across ${ocheList.length} oches`
        : `session across ${ocheList.length} oches`;
      lines.push(
        `It was a lively ${day} ${timeOfDay} ${ocheText} with ${completedGames.length} game${completedGames.length !== 1 ? "s" : ""} on the board.`,
      );
    } else {
      lines.push(socialDartsOpening);
    }

    // Session kickoff - check for media before first game
    if (mediaCounts.firstMedia) {
      lines.push(
        `You kicked off with a ${mediaCounts.firstMedia.type.toLowerCase()}, before anyone had even warmed up.`,
      );
    } else {
      // No media before first game, mention the first game
      const firstGame = completedGames[0];
      lines.push(`You kicked off with ${capitalize(firstGame.name)}, setting a spirited tone for the players.`);
    }

    // Big battle detection
    const longestGame = getLongestGame(completedGames);

    if (longestGame && longestGame.duration >= 10)
      lines.push(
        `One game of ${capitalize(longestGame.game.name)} stood out as a marathon, keeping everyone on edge for over 10 minutes!`,
      );

    // Top winner commentary
    if (state.type === 1) {
      for (const teams of state.teams) {
        if (teams.final_result === 1) {
          const winningTeam = capitalize(teams.name);
          lines.push(
            `${BR_MARKER}${BR_MARKER}The ${sessionTypeText} was fiercely contested, with players battling it out for the top spot and the ${BOLD_OPEN}${winningTeam}${BOLD_CLOSE} team ultimately claiming victory.`,
          );
        }
      }
    } else {
      const sortedPlayers = [...state.parsedPlayers].sort((a, b) => b.points - a.points);
      const topPlayer = sortedPlayers[0];
      const secondPlayer = sortedPlayers[1];

      if (topPlayer) {
        let winnerText = "";
        if (secondPlayer) {
          const margin = topPlayer.points - secondPlayer.points;
          if (margin === 0) winnerText = " — a nail-biting tie at the top.";
          else if (margin === 1) winnerText = " — edging out the competition by a single point.";
          else if (margin <= 2) winnerText = " — holding on with a narrow lead.";
          else winnerText = ` — a solid victory with a ${margin}-point advantage.`;
        }

        lines.push(
          `${BR_MARKER}${BR_MARKER}Leading the pack, ${BOLD_OPEN}${capitalize(topPlayer.name)}${BOLD_CLOSE} claimed the top spot with ${BOLD_OPEN}${topPlayer.points} point${topPlayer.points !== 1 ? "s" : ""}${BOLD_CLOSE}${winnerText}`,
        );
      }
    }

    // Media summary
    const mediaParts = [];

    if (mediaCounts.groupPhotos > 0) {
      mediaParts.push(
        makeLinkMarker(
          "#group-photos",
          `${mediaCounts.groupPhotos} group photo${mediaCounts.groupPhotos > 1 ? "s" : ""}`,
        ),
      );
    }

    if (mediaCounts.loopcams > 0) {
      mediaParts.push(
        makeLinkMarker("#loop-cams", `${mediaCounts.loopcams} loop cam${mediaCounts.loopcams > 1 ? "s" : ""}`),
      );
    }

    if (totalReplays > 0) {
      // if (state.type !== 2 && totalReplays > 10) {
      //   mediaParts.push(makeLinkMarker("#action-replays", `10 action replays`));
      // } else
      mediaParts.push(makeLinkMarker("#action-replays", `${totalReplays} action replay${totalReplays > 1 ? "s" : ""}`));
    }

    if (mediaParts.length > 0) {
      // Join with commas, add “and” before the last item if more than one
      const last = mediaParts.pop();
      const mediaText = mediaParts.length > 0 ? `${mediaParts.join(", ")} and ${last}` : last;
      lines.push(`${BR_MARKER}${BR_MARKER}Fancy a quick replay? Your Story's got ${mediaText} waiting below!`);
    }

    return lines.join(" ");
  }, [completedGames, state.parsedPlayers, mediaCounts, state.oche, state.teams, state.type, totalReplays]);

  return (
    <div className={`${styles.gameCommentary} section section--padded`}>
      <div>
        <p>{renderRichMarkers(commentary)}</p>
      </div>
    </div>
  );
};

export default GameCommentary;
